import { useCallback, useMemo, type ComponentType, type CSSProperties, type ReactNode, type SVGProps } from 'react';
import { useIntl } from 'react-intl';
import { StatusBadge } from '@immo/ui-kit';
import { isEmailTemplatePlaceholder, type EmailTemplateSummary } from '@immo/api-contract';
import { toPlainLabel } from '@immo/components';
import { CalendarIcon, MembersIcon, MailIcon, TagIcon, TextIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';

export const EMAIL_TEMPLATE_COLUMN_IDS = ['name', 'area', 'subject', 'updatedAt', 'updatedBy'] as const;
export type EmailTemplateColumnId = (typeof EMAIL_TEMPLATE_COLUMN_IDS)[number];
export const ALWAYS_VISIBLE_EMAIL_TEMPLATE_COLUMN: EmailTemplateColumnId = 'name';
// Pinned sticky at `left: 0`, which is only correct because the lock keeps it first.
export const LOCKED_EMAIL_TEMPLATE_COLUMNS: ReadonlyArray<EmailTemplateColumnId> = ['name'];

export type EmailTemplateColumn = {
  readonly id: EmailTemplateColumnId;
  readonly label: string;
  readonly icon?: ComponentType<SVGProps<SVGSVGElement>>;
  readonly headClassName?: string;
  readonly headStyle: CSSProperties;
  readonly cellClassName?: string;
  readonly cellStyle?: CSSProperties;
  /** What the column spends of the table's `min-width`: its fixed width, or its floor. */
  readonly budgetPx: number;
  readonly cell: (template: EmailTemplateSummary) => ReactNode;
};

/*
 * The width budget, as `document-template-columns` states it: sized columns claim
 * their share first, Name and Aktualisiert von take a floor, Betreff takes the rest
 * (it budgets 170 for the table's `min-width`), and the table's `min-width` is the sum
 * over the VISIBLE columns plus „Aktionen". The widths are inline so a test can assert
 * a computed style rather than a class string (BL-034).
 */
export const COLUMN_WIDTHS_PX = {
  area: 130,
  updatedAt: 170,
  actions: 170,
} as const;

const NAME_MIN_WIDTH_PX = 320;
const SUBJECT_BUDGET_PX = 170;
const UPDATED_BY_MIN_WIDTH_PX = 170;

// A pinned cell needs an opaque background, or the scrolling columns show through beneath it.
const PINNED_NAME_STYLE = { position: 'sticky', left: 0, minWidth: NAME_MIN_WIDTH_PX } as const satisfies CSSProperties;
const PINNED_HEAD_CELL_CLASS = 'z-20 bg-neutral-50';
const PINNED_BODY_CELL_CLASS = 'z-10 bg-surface';

/** The table's floor: every visible column's width or floor, plus „Aktionen" (outside the arrangement). */
export function tableMinWidthPx(visible: ReadonlyArray<EmailTemplateColumn>): number {
  return visible.reduce<number>((total, column) => total + column.budgetPx, COLUMN_WIDTHS_PX.actions);
}

/** A placeholder key as its German name without braces; a key outside the catalogue stays as it is. */
export function usePlaceholderLabel(): (key: string) => string {
  const t = useTranslations();

  return useCallback(
    (key: string) => (isEmailTemplatePlaceholder(key) ? t(`common.email-templates.placeholders.${key}`) : key),
    [t],
  );
}

export function useEmailTemplateColumns(): ReadonlyArray<EmailTemplateColumn> {
  const t = useTranslations('pages.settings.email-templates');
  const tRoot = useTranslations();
  const tDocuments = useTranslations('pages.settings.document-templates');
  const intl = useIntl();
  const placeholderLabel = usePlaceholderLabel();

  return useMemo<ReadonlyArray<EmailTemplateColumn>>(
    () => [
      {
        id: 'name',
        label: t('columns.name'),
        icon: TextIcon,
        headClassName: PINNED_HEAD_CELL_CLASS,
        headStyle: PINNED_NAME_STYLE,
        cellClassName: PINNED_BODY_CELL_CLASS,
        cellStyle: PINNED_NAME_STYLE,
        budgetPx: NAME_MIN_WIDTH_PX,
        cell: (template) => (
          <div className="min-w-0 truncate font-medium text-text-primary" title={template.name}>
            {template.name}
          </div>
        ),
      },
      {
        id: 'area',
        label: t('columns.area'),
        icon: TagIcon,
        headStyle: { width: COLUMN_WIDTHS_PX.area },
        budgetPx: COLUMN_WIDTHS_PX.area,
        cell: (template) => (
          <StatusBadge size="sm" tone="neutral" label={tRoot(`common.email-templates.areas.${template.area}`)} />
        ),
      },
      {
        id: 'subject',
        label: t('columns.subject'),
        icon: MailIcon,
        headStyle: {},
        cellClassName: 'text-text-secondary',
        budgetPx: SUBJECT_BUDGET_PX,
        cell: (template) => {
          const subject = toPlainLabel(template.subject, placeholderLabel);

          return (
            <div className="truncate" title={subject}>
              {subject}
            </div>
          );
        },
      },
      {
        id: 'updatedAt',
        label: t('columns.updated-at'),
        icon: CalendarIcon,
        headStyle: { width: COLUMN_WIDTHS_PX.updatedAt },
        cellClassName: 'text-text-tertiary',
        budgetPx: COLUMN_WIDTHS_PX.updatedAt,
        cell: (template) =>
          tDocuments('updated-at-value', {
            date: intl.formatDate(template.updatedAt, { day: '2-digit', month: '2-digit', year: 'numeric' }),
            time: intl.formatTime(template.updatedAt, { hour: '2-digit', minute: '2-digit' }),
          }),
      },
      {
        id: 'updatedBy',
        label: t('columns.updated-by'),
        icon: MembersIcon,
        headStyle: { minWidth: UPDATED_BY_MIN_WIDTH_PX },
        cellClassName: 'text-text-tertiary',
        budgetPx: UPDATED_BY_MIN_WIDTH_PX,
        cell: (template) => {
          const name =
            template.updatedByMemberId === null ? t('immoos') : (template.updatedByName ?? tDocuments('no-creator'));

          return (
            <div className="truncate" title={name}>
              {name}
            </div>
          );
        },
      },
    ],
    [t, tRoot, tDocuments, intl, placeholderLabel],
  );
}
